/**
 * Money/quantity fields serialize as JSON *strings*, not numbers.
 *
 * `backend/BACKEND_ARCHITECTURE.md` ("API surface" section) is explicit about this:
 * Pydantic's `Decimal` fields (e.g. `InventoryItem.retail_price`, `Sale.grand_total`,
 * `TransactionLeg.debit`) serialize to JSON as strings like `"3000.00"`, never as JSON
 * numbers — this avoids floating-point round-trip corruption of money at the wire
 * boundary. Every money/quantity field in this package is therefore typed as
 * `DecimalString`, a branded string type, rather than `number`. The brand exists so a
 * plain `string` can't be assigned to a money field by accident (and so a stray
 * `Number(sale.grand_total)` in application code stands out as clearly wrong at a type
 * level) — construct one via `asDecimalString()` (only for values you already know are
 * numeric, e.g. hardcoded test fixtures) or by round-tripping through `Money`.
 *
 * Frontend code must NEVER do float arithmetic on these fields directly
 * (`Number(a) + Number(b)` is exactly the bug this type exists to prevent). Use the
 * `Money` helpers below, which wrap `decimal.js`, for any arithmetic, comparison, or
 * display formatting.
 */
import Decimal from "decimal.js";
/**
 * Wrap a string you already know is a valid decimal literal (e.g. a hardcoded fixture,
 * or a value freshly read from a backend JSON response) as a `DecimalString`. Does not
 * validate — validation happens implicitly the first time it's fed through `Money`.
 */
export function asDecimalString(value) {
    return value;
}
/**
 * Safe-arithmetic helpers for `DecimalString` fields. Every function takes and returns
 * `DecimalString` (or a `Decimal` for intermediate chained math), never `number`, so a
 * caller can't accidentally reintroduce float imprecision partway through a calculation.
 *
 * Example — computing a sale's change due without touching `number`:
 * ```ts
 * import { Money } from "@shop-ledger/shared-types";
 *
 * const tendered = Money.of("50.00");
 * const total = Money.of(sale.grand_total);
 * const change = Money.subtract(tendered, total); // DecimalString, e.g. "12.50"
 * ```
 */
export const Money = {
    /** Parse a `DecimalString` (or a raw numeric literal string) into a `decimal.js` value. */
    of(value) {
        return new Decimal(value);
    },
    add(a, b) {
        return asDecimalString(Money.of(a).plus(Money.of(b)).toFixed(2));
    },
    subtract(a, b) {
        return asDecimalString(Money.of(a).minus(Money.of(b)).toFixed(2));
    },
    multiply(a, b) {
        return asDecimalString(Money.of(a).times(Money.of(b)).toFixed(2));
    },
    /** Sum a list of DecimalStrings (e.g. sale line totals). Returns "0.00" for an empty list. */
    sum(values) {
        return values.reduce((acc, v) => Money.add(acc, v), asDecimalString("0.00"));
    },
    /** -1 / 0 / 1, same contract as `Array.prototype.sort` comparators. */
    compare(a, b) {
        return Money.of(a).comparedTo(Money.of(b));
    },
    isZero(value) {
        return Money.of(value).isZero();
    },
    isNegative(value) {
        return Money.of(value).isNegative();
    },
    isPositive(value) {
        return Money.of(value).isPositive();
    },
    zero() {
        return asDecimalString("0.00");
    },
    /** Round/normalize to `dp` decimal places (default 2, matching `Numeric(12, 2)` columns). */
    round(value, dp = 2) {
        return asDecimalString(Money.of(value).toFixed(dp));
    },
    /**
     * Locale-aware display formatting, e.g. `Money.format(item.retail_price, "UGX")` ->
     * `"UGX 3,000.00"`. Uses `Intl.NumberFormat` under the hood; pass `Tenant.currency`
     * (ISO 4217, e.g. "UGX", "USD") as the currency code.
     */
    format(value, currency, locale) {
        const amount = Money.of(value).toNumber();
        try {
            return new Intl.NumberFormat(locale, { style: "currency", currency }).format(amount);
        }
        catch {
            // Intl throws on an unrecognized ISO 4217 code — degrade to a plain label rather
            // than crash the till UI over a currency-formatting edge case.
            return `${currency} ${Money.of(value).toFixed(2)}`;
        }
    },
};
/** Re-exported so callers needing the raw `decimal.js` API for edge cases can reach it
 * without adding their own `decimal.js` dependency (and risking a version mismatch). */
export { Decimal };
//# sourceMappingURL=decimal.js.map